import React, { Fragment } from 'react';
import { useLocation, useHistory, useRouteMatch } from 'react-router-dom';
import qs from 'query-string';
import Helmet from 'react-helmet';
import { Formik } from 'formik';
import { useQuery } from 'react-apollo';

import SignupAlbumLayout from '../../../components/global/SignupAlbumLayout';
import FullScreenLoader from '../../../components/global/FullScreenLoader';
import PinVerificationStep from './components/PinVerificationStep';
import useMarketingAccountOnboarding from './hooks/useMarketingAccountOnboarding';
import useOpenAlert from '../../../hooks/useOpenAlert';
import { AlbumFolderForMarketingAccounts } from '../../../graphql/queries/album-folder';
import { albumForMarketingAccountMatches } from './queries/album';
import { MARKETING_ACCOUNT_ROUTES } from './constants';
import { getAsset } from '../../../util/assets';
import { getErrorString } from '../../../util/normalize-errors';
import { gtm } from '../../../util/analytics';
import { BROADCAST_TYPE_ENUM } from '../../../constants/broadcasts';
import { ALBUM_FOLDER_JOIN_TYPE } from '../../../constants/album-folder';

const MarketingAccountOnboardingView = () => {
  const location = useLocation();
  const history = useHistory();
  const openAlert = useOpenAlert();
  // The matches flow is picked from the route, not the params: both links
  // carry the same marketing account + email, only the destination differs.
  const matchesRouteMatch = useRouteMatch({
    path: MARKETING_ACCOUNT_ROUTES.MATCHES,
    exact: true,
  });
  const isMatchesFlow = Boolean(matchesRouteMatch);
  const {
    firstName,
    emailAddress,
    albumFolderId,
    albumId,
    accountId: marketingAccountId,
    broadcastType,
  } = qs.parse(location.search ?? '');

  const {
    loading: onboardingLoading,
    onPinVerify,
    onPinCodeResend,
    onMarketingAccountLink,
    onShutterbugRoleEnsure,
  } = useMarketingAccountOnboarding({ emailAddress });

  const { data: { albumFolder } = {}, loading: albumFolderLoading } = useQuery(
    AlbumFolderForMarketingAccounts,
    {
      variables: { id: albumFolderId },
      skip: isMatchesFlow || !albumFolderId,
    },
  );

  const { data: { album } = {}, loading: albumLoading } = useQuery(
    albumForMarketingAccountMatches,
    {
      variables: { id: albumId },
      skip: !isMatchesFlow || !albumId,
    },
  );

  const orgLogo = isMatchesFlow
    ? album?.album_folder?.account?.logo_image?.url
    : albumFolder?.account?.logo_image?.url;

  const onMatchesRedirect = async () => {
    await onShutterbugRoleEnsure();

    // Picture day and join-all folders surface matches at the folder level,
    // every other album at the album itself.
    const entityId =
      album?.album_folder?.picture_day_enabled ||
      album?.album_folder?.join_type === ALBUM_FOLDER_JOIN_TYPE.ALL
        ? (album?.album_folder?.id ?? albumId)
        : albumId;

    history.push(`/app/shutterbug/hierarchy-items/${entityId}/matches`);
  };

  const onOnboardingRedirect = () => {
    if (broadcastType === BROADCAST_TYPE_ENUM.SELFIE_REQUEST.toLowerCase()) {
      history.push(`/upload-selfie/${albumFolderId}`);
      return;
    }

    history.push(`/discover/album-folder/${albumFolderId}`);
  };

  const onSignIn = async () => {
    try {
      if (marketingAccountId) {
        await onMarketingAccountLink(marketingAccountId);
      }

      if (isMatchesFlow) {
        await onMatchesRedirect();
        return;
      }

      onOnboardingRedirect();
    } catch (e) {
      openAlert({
        message: 'There was an error linking identities',
        info: getErrorString(e),
      });
    }
  };

  const onSubmit = async values => {
    try {
      const result = await onPinVerify(values);
      if (result?.data?.accountLoginByEmail?.success) {
        if (isMatchesFlow) {
          gtm.trackEvent({
            type: 'MarketingAccountMatchesRegistration',
            action: null,
          });
        }
        await onSignIn();
      }
    } catch (e) {
      openAlert({
        message: 'There was an error linking identities',
        info: getErrorString(e),
      });
    }
  };

  if (albumFolderLoading || albumLoading) {
    return <FullScreenLoader />;
  }

  return (
    <Fragment>
      <Helmet>
        <title>Waldo &ndash; Verify Email</title>
      </Helmet>
      <div className="marketing-account-onboarding-wrapper">
        <div className="d-flex justify-content-between align-items-center p-3">
          <div className="org-logo">
            {orgLogo && (
              <img
                src={orgLogo}
                alt="Organization Logo"
                style={{ height: '40px' }}
              />
            )}
          </div>
          <div className="waldo-logo">
            <img
              src={getAsset('/powered-by-waldo-blue.svg')}
              alt="Waldo"
              style={{ height: '30px' }}
            />
          </div>
        </div>

        <SignupAlbumLayout.Wrapper
          imageDisabled
          logoSrc={null}
          withCTA={false}
          className="marketing-account-onboarding-layout"
        >
          <div className="marketing-account-onboarding-content-container">
            <Formik
              onSubmit={onSubmit}
              initialValues={{ code: '' }}
              render={({ values, handleSubmit, handleChange }) => (
                <PinVerificationStep
                  emailAddress={emailAddress}
                  firstName={firstName}
                  loading={onboardingLoading}
                  handleSubmit={handleSubmit}
                  handleChange={handleChange}
                  onPinCodeResend={onPinCodeResend}
                  values={values}
                  submitLabel={
                    isMatchesFlow
                      ? 'Verify email and view photos'
                      : 'Verify email and join event'
                  }
                />
              )}
            />
          </div>
        </SignupAlbumLayout.Wrapper>
      </div>
    </Fragment>
  );
};

export default MarketingAccountOnboardingView;
